/**
 * Sentences for the Sentence Counter: strings, not offsets (text-analysis/segment.ts is
 * the span-based splitter the highlighting tools use).
 *
 * A sentence ends at a full stop, exclamation mark or question mark — with any closing
 * quotation marks or brackets that follow it — when the next thing is a capital letter or
 * an opening quotation mark, or when the text or the line ends. Decimals ("3.5") and web
 * addresses have no space after the point, so they never end one. A line that ends in one of
 * those marks ends a sentence too. What is left is deciding whether a full stop after a
 * short word is an abbreviation:
 *
 * - **Always an abbreviation** ("Dr. Lee", "Fig. A", "St. Louis"): never a break.
 * - **Ambiguous** ("no.", "etc.", "max."): these end real sentences ("I said no. Then I
 *   left."), so they are a break when a capital follows. "No. 5" and "etc. and" are not
 *   breaks anyway, because a number or a lower-case word follows.
 * - **Dotted initialisms** ("U.S.", "U.K.", "a.m."): "the U.S. Army" goes on, "in the
 *   U.S. He left" does not, so they break only before a word that usually opens a
 *   sentence (a pronoun, an article, a conjunction).
 * - **A single capital letter** ("J. K. Rowling") is an initial, except "I".
 */

const TERMINAL_RE = /[.!?]+["'”’)\]]*/g;
const LAST_CHUNK_RE = /[\p{L}.]+$/u;
const NEXT_START_RE = /^\s+(?:["'“‘«]|\p{Lu})/u;
const NEXT_WORD_RE = /^\s+["'“‘«]?(\p{L}+)/u;
const LINE_END_RE = /^[ \t]*(?:\r?\n|$)/;
const SINGLE_CAPITAL_RE = /^\p{Lu}$/u;

/** Followed by a capital, these are still part of the sentence. */
const ALWAYS_ABBREVIATION = new Set([
  "mr",
  "mrs",
  "ms",
  "dr",
  "prof",
  "sr",
  "jr",
  "vs",
  "fig",
  "figs",
  "eq",
  "eqs",
  "pp",
  "vol",
  "vols",
  "ch",
  "sec",
  "dept",
  "univ",
  "approx",
  "govt",
  "inc",
  "ltd",
  "corp",
  "co",
  "gen",
  "col",
  "lt",
  "sgt",
  "capt",
  "gov",
  "sen",
  "rep",
  "hon",
  "rev",
  "mt",
  "st",
  "ed",
  "eds",
  "cf",
  "viz",
  "e.g",
  "i.e",
]);

/** Words that really do end sentences, so a capital after them starts a new one. */
const AMBIGUOUS_ABBREVIATION = new Set([
  "no",
  "nos",
  "etc",
  "max",
  "min",
  "est",
]);

/** Capitalised words that open a sentence far more often than they name something. */
const SENTENCE_STARTERS = new Set([
  "a",
  "after",
  "all",
  "also",
  "although",
  "an",
  "and",
  "as",
  "at",
  "because",
  "before",
  "but",
  "by",
  "despite",
  "during",
  "even",
  "for",
  "from",
  "he",
  "her",
  "here",
  "his",
  "how",
  "however",
  "i",
  "if",
  "in",
  "it",
  "its",
  "many",
  "most",
  "my",
  "no",
  "not",
  "now",
  "of",
  "on",
  "one",
  "or",
  "our",
  "she",
  "since",
  "so",
  "some",
  "still",
  "such",
  "that",
  "the",
  "their",
  "then",
  "there",
  "these",
  "they",
  "this",
  "those",
  "to",
  "two",
  "we",
  "what",
  "when",
  "where",
  "which",
  "while",
  "who",
  "why",
  "with",
  "yes",
  "you",
  "your",
]);

/** Is the single full stop that ends `before` part of an abbreviation, not a sentence end? */
function continuesSentence(before: string, after: string): boolean {
  const chunk = LAST_CHUNK_RE.exec(before)?.[0] ?? "";
  if (chunk === "") {
    return false;
  }
  const key = chunk.toLowerCase();
  if (ALWAYS_ABBREVIATION.has(key)) {
    return true;
  }
  if (AMBIGUOUS_ABBREVIATION.has(key)) {
    return false;
  }
  if (chunk !== "I" && SINGLE_CAPITAL_RE.test(chunk)) {
    return true;
  }
  if (key.includes(".")) {
    const next = NEXT_WORD_RE.exec(after)?.[1]?.toLowerCase();
    return next === undefined || !SENTENCE_STARTERS.has(next);
  }
  return false;
}

function endsSentence(text: string, start: number, end: number): boolean {
  const after = text.slice(end);
  if (!(LINE_END_RE.test(after) || NEXT_START_RE.test(after))) {
    return false;
  }
  const bareFullStop = text.slice(start, end) === ".";
  return !(bareFullStop && continuesSentence(text.slice(0, start), after));
}

/** The sentences of `text`, trimmed, in order. */
export function splitSentenceStrings(text: string): string[] {
  const sentences: string[] = [];
  let from = 0;
  for (const match of text.matchAll(TERMINAL_RE)) {
    const end = match.index + match[0].length;
    if (endsSentence(text, match.index, end)) {
      sentences.push(text.slice(from, end));
      from = end;
    }
  }
  sentences.push(text.slice(from));
  return sentences.map((sentence) => sentence.trim()).filter(Boolean);
}
